import baseAPI, { IdentityUserView, OtpChallengeView, TokenResultView } from 'api';
import clsx from 'clsx';
import { ErrorMessage } from 'components/Inputs/components/ErrorMessage';
import { PrevButton } from 'components/PrevButton';
import { WisoryButton } from 'components/WisoryButton';
import useAuthentication from 'hooks/useAuthentication';
import { BookingFormLayout } from 'pages/profile/components/Layout';
import { ProfileFooter } from 'pages/profile/components/Layout/components/ProfileFooter';
import { useEffect, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import OtpInput from 'react-otp-input-rc-17';
import styles from './styles.module.css';
import { setToken } from '@wisory-international-ab/login-frontend';

type Props = {
  title: string;
  useCognitoOtpLogin: boolean;
  submittedEmail: string;
  otpData?: OtpChallengeView;
  onSubmit: (data: IdentityUserView) => void;
  onGoBack: VoidFunction;
};

type FormParams = { code: string };

const resendDuration = 60;

export function CodeForm({ title, submittedEmail, otpData, useCognitoOtpLogin, onSubmit, onGoBack }: Props) {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState<boolean>(false);
  const { doLogin, doOtpLogin } = useAuthentication();
  const [errorMessage, setErrorMessage] = useState<string>();
  const { control, formState, handleSubmit } = useForm<FormParams>();
  const [countdown, setCountdown] = useState<number>(0);
  const timerRef = useRef<NodeJS.Timeout | undefined>();

  useEffect(() => {
    if (countdown === resendDuration && !timerRef.current) {
      timerRef.current = setInterval(() => {
        setCountdown((newCount) => newCount - 1);
      }, 1000);
    }

    if (countdown === 0 && timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = undefined;
    }
  }, [countdown, setCountdown]);

  const onFormSubmit = async ({ code }: FormParams) => {
    if (!submittedEmail) {
      return;
    }

    setSubmitting(true);

    try {
      let token: TokenResultView;
      if (useCognitoOtpLogin && otpData) {
        token = await doOtpLogin(submittedEmail, otpData.session!, otpData.challengeName!, code);
      } else {
        token = await doLogin(submittedEmail, code);
      }

      setToken(token as any);
      const userInfo = await baseAPI.api.getMyAccount();
      // setToken(us)
      onSubmit(userInfo.data);
    } catch (error) {
      setErrorMessage(t('Invalid OTP code'));
    }

    setSubmitting(false);
  };

  const onResendClick = (): void => {
    setErrorMessage(undefined);
    setCountdown(resendDuration);
    baseAPI.login.sendOtp({
      emailAddress: submittedEmail,
    });
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit)}>
      <BookingFormLayout
        footer={
          <ProfileFooter className="flex justify-between">
            <PrevButton type="button" loading={submitting} onClick={onGoBack}>
              {t('REACT_BOOKING_FRONTEND.common.prevButton')}
            </PrevButton>
            <WisoryButton variant="primary" type="submit" loading={submitting}>
              {t('REACT_BOOKING_FRONTEND.booking2.loginSectionBtn')}
            </WisoryButton>
          </ProfileFooter>
        }
      >
        <div className="wisory-title mb-2">{title}</div>
        <div className="font-openSans mb-10 text-black">
          {t('REACT_BOOKING_FRONTEND.checkoutScreen.payWithCredit.otpSubtitle')}
        </div>
        <div className="text-left">
          <div className="flex justify-center w-full">
            <Controller
              control={control}
              name="code"
              render={({ field }) => (
                <OtpInput
                  className={styles.otpInput}
                  shouldAutoFocus={!isMobile}
                  numInputs={6}
                  isInputNum
                  value={field.value}
                  onChange={(e: string) => field.onChange(e)}
                />
              )}
              rules={{
                required: t('REACT_BOOKING_FRONTEND.common.validations.required'),
                minLength: {
                  value: 6,
                  message: t('REACT_BOOKING_FRONTEND.common.validations.required'),
                },
              }}
            />
          </div>
        </div>

        {formState.errors.code?.message && (
          <div className="text-right">
            <ErrorMessage>{formState.errors.code?.message}</ErrorMessage>
          </div>
        )}

        {errorMessage && (
          <div
            className={clsx(
              'animate__animated animate__shakeX p-3 text-sm rounded-sm mt-2 mb-2 text-error text-center',
              styles.error
            )}
          >
            {errorMessage}
          </div>
        )}

        <div className="mt-12 text-sm font-openSans text-black">
          <span>{t('REACT_BOOKING_FRONTEND.booking2.codeScreenResendCodeLabel')}</span>
          &nbsp;
          {countdown > 0 &&
            t('REACT_BOOKING_FRONTEND.booking2.codeScreenResendCodeCount', {
              countdown,
            })}
          {countdown <= 0 && (
            <span className={'underline cursor-pointer text-yellow hover:no-underline'} onClick={onResendClick}>
              {t('REACT_BOOKING_FRONTEND.booking2.codeScreenResendCodeBtn')}
            </span>
          )}
        </div>
      </BookingFormLayout>
    </form>
  );
}
